import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../../services/ApiService';
import { useOnboardingStore } from '../../stores/useOnboardingStore';
import { useFocusTrap } from '../../hooks/useFocusTrap.ts';
import { PecktreeLogo } from '../shared/PecktreeLogo.tsx';
import type { Gender, Project, Person } from '../../types';
import { relatieItemVoor } from '../../utils/relatieRichting.ts';
import { isGlobaalGemeld } from '../../services/api/base.ts';
import { beschrijfFout, bestaandePersoon, foutMelding } from '../../utils/foutUitleg.ts';
import { slaPersoonOp } from '../../services/dataSync.ts';
import { formatFullName } from '../../utils/nameUtils.ts';
import { useToast } from '../shared/Toast.tsx';

/**
 * #1825 — zelfde opslag als savePersonWithCache (een herhaling na een time-out zet latere invoer
 * er alsnog op, zonder een collega te overschrijven), maar zonder de store: die hoort nog bij
 * het vorige project.
 */
const slaWizardPersoonOp = async (...args: Parameters<typeof slaPersoonOp>): Promise<Person> => {
  const { bestond: _bestond, warnings: _warnings, ...persoon } = await slaPersoonOp(...args);
  return persoon as Person;
};

interface OnboardingWizardProps {
  t: (key: string, params?: Record<string, string>) => string;
  language: string;
}

type ProbandChoice = 'self' | 'other' | null;

interface StepState {
  // Step 2 — Project
  projectName: string;
  projectDesc: string;
  // Step 3 — Proband
  probandChoice: ProbandChoice;
  probandGivenNames: string;
  probandSurname: string;
  probandGender: Gender;
  probandBirthDate: string;
  // Step 4 — Parents
  fatherGivenNames: string;
  fatherSurname: string;
  fatherBirthDate: string;
  motherGivenNames: string;
  motherSurname: string;
  motherBirthDate: string;
}

const TOTAL_STEPS = 5;

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ t, language }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Created entities tracked across steps
  const createdProject = useRef<Project | null>(null);
  const createdProband = useRef<Person | null>(null);
  const personCount = useRef(0);
  // #1825 — één clientId per persoon per wizard-sessie: een herhaling na een
  // time-out krijgt van de server de al gemaakte persoon terug, geen tweede.
  const clientIds = useRef({
    proband: crypto.randomUUID(),
    father: crypto.randomUUID(),
    mother: crypto.randomUUID(),
  });
  const createdParents = useRef<{ father?: Person; mother?: Person }>({});

  // Trap keyboard focus inside the wizard dialog while it is visible (a11y).
  const focusTrapRef = useFocusTrap(visible);

  const [form, setForm] = useState<StepState>({
    projectName: '',
    projectDesc: '',
    probandChoice: null,
    probandGivenNames: '',
    probandSurname: '',
    probandGender: 'U',
    probandBirthDate: '',
    fatherGivenNames: '',
    fatherSurname: '',
    fatherBirthDate: '',
    motherGivenNames: '',
    motherSurname: '',
    motherBirthDate: '',
  });

  const updateForm = useCallback(<K extends keyof StepState>(key: K, value: StepState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError(null);
  }, []);

  // Fade transition helper
  const goToStep = useCallback((nextStep: number) => {
    setTransitioning(true);
    setTimeout(() => {
      setStep(nextStep);
      setTransitioning(false);
    }, 200);
  }, []);

  const handleSkip = useCallback(async () => {
    await useOnboardingStore.getState().skipWizard();
  }, []);

  // #1760 — gebruiker heeft al een GEDCOM: wizard afsluiten zonder project aan te maken en
  // naar de bestaande import (/import maakt zelf het project aan).
  const handleGedcomImport = useCallback(async () => {
    await useOnboardingStore.getState().completeWizard();
    navigate('/import');
  }, [navigate]);

  const handleComplete = useCallback(async () => {
    const store = useOnboardingStore.getState();
    await store.completeWizard();

    // Mark checklist items completed by the wizard
    if (createdProject.current) {
      await store.completeChecklistItem('projectCreated');
    }
    if (createdProband.current) {
      await store.completeChecklistItem('probandSet');
    }

    // Dispatch event so App.tsx can activate the project through its own
    // handleSelectProject flow (which uses hook-bound setters that trigger
    // proper React re-renders). Direct store updates via getState() don't
    // reliably propagate when this component is being unmounted.
    if (createdProject.current) {
      localStorage.setItem('af_current_project', createdProject.current.id);
      ApiService.setLastProject(createdProject.current.id);

      window.dispatchEvent(
        new CustomEvent('pecktree:wizard-complete', {
          detail: { projectId: createdProject.current.id },
        })
      );
    }

    navigate('/tree');
  }, [navigate]);

  // Step 2: Create project
  const handleCreateProject = useCallback(async () => {
    if (!form.projectName.trim()) {
      setError(t('onboarding_project_name_required'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const project = await ApiService.createProject(form.projectName.trim(), {
        description: form.projectDesc.trim(),
      });
      createdProject.current = project;
      goToStep(3);
    } catch (err: unknown) {
      console.error('Onboarding create project failed:', err instanceof Error ? err.message : err);
      setError(t('onboarding_create_project_failed'));
    } finally {
      setLoading(false);
    }
  }, [form.projectName, form.projectDesc, goToStep, t]);

  // Step 3: Create proband
  const handleCreateProband = useCallback(async () => {
    if (!form.probandGivenNames.trim() || !form.probandSurname.trim()) {
      setError(t('onboarding_proband_name_required'));
      return;
    }
    if (!createdProject.current) return;
    setLoading(true);
    setError(null);
    let persoonOpgeslagen = false;
    try {
      const person = await slaWizardPersoonOp(
        {
          projectId: createdProject.current.id,
          givenNames: form.probandGivenNames.trim(),
          surname: form.probandSurname.trim(),
          gender: form.probandGender,
          birthDate: form.probandBirthDate || undefined,
          isLiving: true,
        },
        // Een 403 meldt de wizard zelf (één melding, geen globale toast erbij).
        { clientId: clientIds.current.proband, zelfMelden: true }
      );
      createdProband.current = person;
      personCount.current = 1;
      persoonOpgeslagen = true;

      // Set as proband
      await ApiService.updateProject(
        createdProject.current.id,
        { proband_id: person.id } as Partial<Project>,
        { zelfMelden: true }
      );

      // Pre-fill parent surname
      if (form.probandSurname.trim()) {
        setForm((prev) => ({
          ...prev,
          fatherSurname: prev.fatherSurname || form.probandSurname.trim(),
        }));
      }

      goToStep(4);
    } catch (err: unknown) {
      console.error('Onboarding create proband failed:', err instanceof Error ? err.message : err);
      // #1825 — herhaalbaar: de proband heeft een vaste clientId (herhaling → 200).
      const naam = formatFullName(
        form.probandGivenNames.trim(),
        undefined,
        form.probandSurname.trim()
      );
      const ctx = {
        soort: 'persoon',
        scherm: 'wizard',
        persoon: naam,
        knop: t('onboarding_next'),
        herhaalbaar: true,
      } as const;
      if (persoonOpgeslagen) {
        // De persoon staat er; alleen het instellen als proband mislukte.
        const { waarom, watNu } = beschrijfFout(err, ctx, t);
        setError(
          [
            // Na een time-out weten we niet of het instellen toch lukte.
            t(
              (err as { code?: string })?.code === 'REQUEST_TIMEOUT'
                ? 'onboarding_proband_onzeker'
                : 'onboarding_proband_niet_ingesteld',
              { naam }
            ),
            waarom,
            watNu,
            t('onboarding_proband_later', { menu: t('setAsProband') }),
          ].join(' ')
        );
      } else if (bestaandePersoon(err))
        // Een herhaling vond de proband al; alleen de latere aanpassing kwam niet aan.
        setError(
          foutMelding(
            {
              err,
              ctx,
              opgeslagen: [naam],
              nietOpgeslagen: [t('foutUitleg_aanpassingVan', { persoon: naam })],
            },
            t
          )
        );
      else setError(foutMelding({ err, ctx, nietOpgeslagen: [naam] }, t));
    } finally {
      setLoading(false);
    }
  }, [form, goToStep, t]);

  // Step 4: Create parents
  const handleCreateParents = useCallback(async () => {
    if (!createdProject.current || !createdProband.current) return;
    setLoading(true);
    setError(null);
    let bezigMet: 'father' | 'mother' | null = null;
    try {
      const projectId = createdProject.current.id;
      const probandId = createdProband.current.id;

      // Per ouder één request: persoon + ouderrelatie atomair. Een al gemaakte
      // ouder (eerdere poging) wordt niet opnieuw gestuurd en telt één keer.
      const ouders = [
        ['father', 'M', form.fatherGivenNames, form.fatherSurname, form.fatherBirthDate],
        ['mother', 'F', form.motherGivenNames, form.motherSurname, form.motherBirthDate],
      ] as const;
      for (const [kant, gender, voornamen, achternaam, geboorte] of ouders) {
        if (!voornamen.trim() || createdParents.current[kant]) continue;
        bezigMet = kant;
        createdParents.current[kant] = await slaWizardPersoonOp(
          {
            projectId,
            givenNames: voornamen.trim(),
            surname: achternaam.trim(),
            gender: gender as Gender,
            birthDate: geboorte || undefined,
            isLiving: true,
          },
          // #1825 — ook een 403 meldt de wizard zelf: één melding, met wie wel is opgeslagen.
          {
            relationships: [relatieItemVoor(kant, probandId)],
            clientId: clientIds.current[kant],
            zelfMelden: true,
          }
        );
        personCount.current++;
      }

      goToStep(5);
    } catch (err: unknown) {
      console.error('Onboarding create parents failed:', err instanceof Error ? err.message : err);
      // #1825 — eerlijk: wie is opgeslagen, wie niet, waarom en wat nu.
      const ouders = [
        ['father', form.fatherGivenNames, form.fatherSurname],
        ['mother', form.motherGivenNames, form.motherSurname],
      ] as const;
      const namen = (wel: boolean) =>
        ouders
          .filter(([kant, voor]) => voor.trim() && !!createdParents.current[kant] === wel)
          .map(([, voor, achter]) => formatFullName(voor.trim(), undefined, achter.trim()));
      const niet = namen(false);
      // Een herhaling vond deze ouder al: hij staat er, alleen zijn latere aanpassing niet.
      // Hij blijft "niet gemaakt" (createdParents), zodat opnieuw opslaan de aanpassing meestuurt.
      const aangepast = bestaandePersoon(err) && bezigMet ? niet[0] : null;
      const tekst = foutMelding(
        {
          err,
          ctx: {
            soort: 'ouders',
            scherm: 'wizard',
            persoon: niet[0],
            knop: t('onboarding_finish'),
            herhaalbaar: true,
          },
          ...(aangepast
            ? {
                opgeslagen: [...namen(true), aangepast],
                nietOpgeslagen: [t('foutUitleg_aanpassingVan', { persoon: aangepast })],
                nietGeprobeerd: niet.slice(1),
              }
            : {
                opgeslagen: namen(true),
                // Vader en moeder gaan na elkaar: de eerste niet-opgeslagen is verstuurd
                // en mislukt, de rest is nooit verstuurd.
                nietOpgeslagen: niet.slice(0, 1),
                nietGeprobeerd: niet.slice(1),
              }),
        },
        t
      );
      setError(tekst);
      // Zijn eigen verzoeken meldt de wizard zelf (zelfMelden); een 403 elders meldde App al.
      if (!isGlobaalGemeld(err)) toast(tekst, 'error');
    } finally {
      setLoading(false);
    }
  }, [form, goToStep, t, toast]);

  // Close overlay with animation
  const closeOverlay = useCallback((callback: () => Promise<void> | void) => {
    return async () => {
      setVisible(false);
      await callback();
    };
  }, []);

  // Determine proband display name
  const probandName = createdProband.current
    ? `${createdProband.current.givenNames} ${createdProband.current.surname}`.trim()
    : form.probandGivenNames
      ? `${form.probandGivenNames} ${form.probandSurname}`.trim()
      : '';

  // Shared input class
  const inputClass =
    'w-full rounded-lg border border-pt-sage/20 bg-white px-4 py-2.5 text-sm text-pt-forest ' +
    'placeholder:text-pt-forest/65 dark:placeholder:text-pt-sage outline-none transition-colors ' +
    'focus:border-[var(--color-pt-gold-text)] focus:ring-2 focus:ring-[var(--color-pt-gold-text)]/20 ' +
    'dark:bg-white/5 dark:border-pt-sage/30 dark:text-pt-cream disabled:opacity-60';

  // #1825 — opgeslagen ouder: grijs vlak + "Opgeslagen ✓", zoals in "Ouders toevoegen".
  const ouderVeldenClass = (opgeslagen: boolean) =>
    // Ruimte altijd gereserveerd (-mx-2 p-2), alleen de achtergrond verschijnt:
    // geen verspringing als een ouder opgeslagen raakt.
    '-mx-2 grid grid-cols-3 gap-3 rounded-lg p-2' +
    (opgeslagen ? ' bg-pt-sage/20 dark:bg-pt-sage/10' : '');
  const opgeslagenLabel = (
    <span className="ml-auto text-xs font-semibold text-pt-forest dark:text-pt-cream">
      {t('addParentsOpgeslagen')}
    </span>
  );

  const labelClass = 'block text-sm font-medium text-pt-forest dark:text-pt-cream mb-1';

  // -- Render helpers per step --

  const renderProgressBar = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {Array.from({ length: TOTAL_STEPS }, (_, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === step;
        const isDone = stepNum < step;
        return (
          <div
            key={stepNum}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              isActive
                ? 'w-8 bg-[var(--color-pt-gold-text)]'
                : isDone
                  ? 'w-2.5 bg-[var(--color-pt-gold-text)]/60'
                  : 'w-2.5 bg-pt-sage/30 dark:bg-pt-sage/20'
            }`}
          />
        );
      })}
      <span className="ml-3 text-xs text-pt-forest/70 dark:text-pt-sage/80 font-medium">
        {step}/{TOTAL_STEPS}
      </span>
    </div>
  );

  const renderStepWelcome = () => (
    <div className="text-center">
      {/* Het gedeelde logo (sidebar, aanmeldscherm) is crème op donker; op de lichte
          modal valt het weg zonder donkergroene cirkel. */}
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-pt-forest">
        <PecktreeLogo size={56} />
      </div>
      <h2 className="text-2xl font-bold text-[var(--color-pt-forest)] dark:text-pt-cream mb-2">
        {t('onboarding_welcome_title')}
      </h2>
      <p className="text-pt-forest/70 dark:text-pt-sage/80 leading-relaxed mb-8">
        {t('onboarding_welcome_subtitle')}
      </p>
      <div className="flex flex-col gap-3">
        <button
          onClick={() => goToStep(2)}
          className="w-full rounded-lg bg-pt-forest px-6 py-3 text-sm font-semibold text-pt-gold transition-colors hover:bg-pt-forest-hover"
        >
          {t('onboarding_start')}
        </button>
        <button
          onClick={closeOverlay(handleGedcomImport)}
          data-testid="onboarding-gedcom"
          className="w-full rounded-lg border border-pt-sage/40 bg-white px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-pt-cream"
        >
          {t('onboarding_have_gedcom')}
        </button>
        <button
          onClick={closeOverlay(handleSkip)}
          className="text-sm text-pt-forest/70 dark:text-pt-sage hover:text-pt-forest dark:hover:text-pt-cream transition-colors"
        >
          {t('onboarding_skip')}
        </button>
      </div>
    </div>
  );

  const renderStepProject = () => (
    <div>
      <h2 className="text-xl font-bold text-[var(--color-pt-forest)] mb-1">
        {t('onboarding_project_title')}
      </h2>
      <p className="text-sm text-pt-forest/70 dark:text-pt-sage/80 mb-5">
        {t('onboarding_project_subtitle')}
      </p>

      <div className="space-y-4">
        <div>
          <label className={labelClass}>{t('onboarding_project_name')}</label>
          <input
            type="text"
            className={inputClass}
            placeholder={t('onboarding_project_name_placeholder')}
            value={form.projectName}
            onChange={(e) => updateForm('projectName', e.target.value)}
            autoFocus
          />
        </div>
        <div>
          <label className={labelClass}>
            {t('onboarding_project_desc')}{' '}
            <span className="text-pt-forest/70 dark:text-pt-sage/80 font-normal">
              ({t('onboarding_optional')})
            </span>
          </label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={3}
            placeholder={t('onboarding_project_desc_placeholder')}
            value={form.projectDesc}
            onChange={(e) => updateForm('projectDesc', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderStepProband = () => (
    <div>
      <h2 className="text-xl font-bold text-[var(--color-pt-forest)] mb-1">
        {t('onboarding_proband_title')}
      </h2>
      <p className="text-sm text-pt-forest/70 dark:text-pt-sage/80 mb-5">
        {t('onboarding_proband_subtitle')}
      </p>

      {/* Choice buttons */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button
          type="button"
          onClick={() => updateForm('probandChoice', 'self')}
          className={`rounded-lg border-2 px-4 py-3 text-sm font-medium transition-colors ${
            form.probandChoice === 'self'
              ? 'border-[var(--color-pt-gold-text)] bg-[var(--color-pt-gold-text)]/5 text-[var(--color-pt-forest)]'
              : 'border-pt-sage/20 text-pt-forest hover:border-pt-sage/40 dark:border-pt-sage/30 dark:text-pt-cream dark:hover:border-pt-sage/50'
          }`}
        >
          {t('onboarding_proband_self')}
        </button>
        <button
          type="button"
          onClick={() => updateForm('probandChoice', 'other')}
          className={`rounded-lg border-2 px-4 py-3 text-sm font-medium transition-colors ${
            form.probandChoice === 'other'
              ? 'border-[var(--color-pt-gold-text)] bg-[var(--color-pt-gold-text)]/5 text-[var(--color-pt-forest)]'
              : 'border-pt-sage/20 text-pt-forest hover:border-pt-sage/40 dark:border-pt-sage/30 dark:text-pt-cream dark:hover:border-pt-sage/50'
          }`}
        >
          {t('onboarding_proband_other')}
        </button>
      </div>

      {form.probandChoice && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>{t('onboarding_given_names')}</label>
              <input
                type="text"
                className={inputClass}
                value={form.probandGivenNames}
                onChange={(e) => updateForm('probandGivenNames', e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className={labelClass}>{t('onboarding_surname')}</label>
              <input
                type="text"
                className={inputClass}
                value={form.probandSurname}
                onChange={(e) => updateForm('probandSurname', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>{t('onboarding_gender')}</label>
              <select
                className={inputClass}
                value={form.probandGender}
                onChange={(e) => updateForm('probandGender', e.target.value as Gender)}
              >
                <option value="M">{t('onboarding_gender_male')}</option>
                <option value="F">{t('onboarding_gender_female')}</option>
                <option value="O">{t('onboarding_gender_other')}</option>
                <option value="U">{t('onboarding_gender_unknown')}</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>
                {t('onboarding_birth_date')}{' '}
                <span className="text-pt-forest/70 dark:text-pt-sage/80 font-normal">
                  ({t('onboarding_optional')})
                </span>
              </label>
              <input
                type="date"
                className={inputClass}
                value={form.probandBirthDate}
                onChange={(e) => updateForm('probandBirthDate', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStepFamily = () => (
    <div>
      <h2 className="text-xl font-bold text-[var(--color-pt-forest)] mb-1">
        {t('onboarding_family_title')}
      </h2>
      <p className="text-sm text-pt-forest/70 dark:text-pt-sage/80 mb-5">
        {t('onboarding_family_subtitle').replace('{name}', probandName)}
      </p>

      {/* Father */}
      {/* #1825 — een opgeslagen ouder wordt niet opnieuw gestuurd: velden dicht. */}
      <fieldset className="mb-5" disabled={!!createdParents.current.father}>
        <legend className="w-full text-sm font-semibold text-[var(--color-pt-forest)] mb-2 flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[var(--color-pt-male)]" />
          {t('onboarding_family_father')}
          <span className="text-pt-forest/70 dark:text-pt-sage/80 font-normal">
            ({t('onboarding_optional')})
          </span>
          {createdParents.current.father && opgeslagenLabel}
        </legend>
        <div className={ouderVeldenClass(!!createdParents.current.father)}>
          <div>
            <input
              type="text"
              className={inputClass}
              placeholder={t('onboarding_given_names')}
              value={form.fatherGivenNames}
              onChange={(e) => updateForm('fatherGivenNames', e.target.value)}
            />
          </div>
          <div>
            <input
              type="text"
              className={inputClass}
              placeholder={t('onboarding_surname')}
              value={form.fatherSurname}
              onChange={(e) => updateForm('fatherSurname', e.target.value)}
            />
          </div>
          <div>
            <input
              type="date"
              className={inputClass}
              value={form.fatherBirthDate}
              onChange={(e) => updateForm('fatherBirthDate', e.target.value)}
            />
          </div>
        </div>
      </fieldset>

      {/* Mother */}
      <fieldset disabled={!!createdParents.current.mother}>
        <legend className="w-full text-sm font-semibold text-[var(--color-pt-forest)] mb-2 flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-[var(--color-pt-ruby)]" />
          {t('onboarding_family_mother')}
          <span className="text-pt-forest/70 dark:text-pt-sage/80 font-normal">
            ({t('onboarding_optional')})
          </span>
          {createdParents.current.mother && opgeslagenLabel}
        </legend>
        <div className={ouderVeldenClass(!!createdParents.current.mother)}>
          <div>
            <input
              type="text"
              className={inputClass}
              placeholder={t('onboarding_given_names')}
              value={form.motherGivenNames}
              onChange={(e) => updateForm('motherGivenNames', e.target.value)}
            />
          </div>
          <div>
            <input
              type="text"
              className={inputClass}
              placeholder={t('onboarding_surname')}
              value={form.motherSurname}
              onChange={(e) => updateForm('motherSurname', e.target.value)}
            />
          </div>
          <div>
            <input
              type="date"
              className={inputClass}
              value={form.motherBirthDate}
              onChange={(e) => updateForm('motherBirthDate', e.target.value)}
            />
          </div>
        </div>
      </fieldset>
    </div>
  );

  const renderStepComplete = () => (
    <div className="text-center">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--color-pt-gold-text)]/10 ring-2 ring-[var(--color-pt-gold-text)]/40">
        <svg
          width="36"
          height="36"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--color-pt-gold-text)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-[var(--color-pt-forest)] dark:text-pt-cream mb-2">
        {t('onboarding_complete_title')}
      </h2>
      <p className="text-pt-forest/70 dark:text-pt-sage/80 leading-relaxed mb-6">
        {t(
          personCount.current === 1
            ? 'onboarding_complete_message_one'
            : 'onboarding_complete_message'
        )
          .replace('{project}', createdProject.current?.name || '')
          .replace('{count}', String(personCount.current))}
      </p>
      <button
        onClick={closeOverlay(handleComplete)}
        className="w-full rounded-lg bg-pt-forest px-6 py-3 text-sm font-semibold text-pt-gold transition-colors hover:bg-pt-forest-hover"
      >
        {t('onboarding_go_to_tree')}
      </button>
    </div>
  );

  // Navigation buttons
  const renderNavigation = () => {
    if (step === 1 || step === 5) return null;

    const canGoNext = (() => {
      switch (step) {
        case 2:
          return form.projectName.trim().length > 0;
        case 3:
          return (
            form.probandChoice !== null &&
            form.probandGivenNames.trim().length > 0 &&
            form.probandSurname.trim().length > 0
          );
        case 4:
          return true; // parents are optional
        default:
          return false;
      }
    })();

    const handleNext = async () => {
      switch (step) {
        case 2:
          await handleCreateProject();
          break;
        case 3:
          await handleCreateProband();
          break;
        case 4:
          await handleCreateParents();
          break;
      }
    };

    return (
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-pt-sage/20 dark:border-pt-sage/30">
        <button
          onClick={() => goToStep(step - 1)}
          className="text-sm text-pt-forest/70 dark:text-pt-sage hover:text-pt-forest dark:hover:text-pt-cream transition-colors"
          disabled={loading}
        >
          {t('onboarding_previous')}
        </button>
        <div className="flex items-center gap-4">
          {step < 5 && (
            <button
              onClick={closeOverlay(handleSkip)}
              className="text-sm text-pt-forest/70 dark:text-pt-sage hover:text-pt-forest dark:hover:text-pt-cream transition-colors"
              disabled={loading}
            >
              {t('onboarding_skip')}
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!canGoNext || loading}
            className={`rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors ${
              canGoNext && !loading
                ? 'bg-pt-forest text-pt-gold hover:bg-pt-forest-hover'
                : 'bg-pt-sage/30 text-pt-sage cursor-not-allowed dark:bg-pt-sage/20'
            }`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                {t('onboarding_loading')}
              </span>
            ) : step === 4 ? (
              t('onboarding_finish')
            ) : (
              t('onboarding_next')
            )}
          </button>
        </div>
      </div>
    );
  };

  // Step content
  const renderStep = () => {
    switch (step) {
      case 1:
        return renderStepWelcome();
      case 2:
        return renderStepProject();
      case 3:
        return renderStepProband();
      case 4:
        return renderStepFamily();
      case 5:
        return renderStepComplete();
      default:
        return null;
    }
  };

  return (
    <div
      ref={focusTrapRef}
      role="dialog"
      aria-modal="true"
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[var(--color-pt-forest)]/80 backdrop-blur-sm dark:bg-black/60" />

      {/* Card */}
      <div
        className={`relative w-full max-w-2xl mx-4 rounded-xl overflow-hidden bg-pt-cream border border-pt-gold/10 shadow-2xl transition-all duration-200 dark:bg-[#0f1a14]/95 dark:border dark:border-white/10 dark:backdrop-blur-md dark:shadow-none ${
          transitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
        }`}
      >
        {/* Gold accent line at top */}
        <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-[var(--color-pt-gold-text)]/60 to-transparent" />

        <div className="p-8">
          {step > 1 && step < 5 && renderProgressBar()}

          {renderStep()}

          {error && (
            <div className="mt-4 rounded-lg bg-[var(--color-pt-ruby)]/5 border border-[var(--color-pt-ruby)]/20 px-4 py-2.5 text-sm text-[var(--color-pt-ruby)]">
              {error}
            </div>
          )}

          {renderNavigation()}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
